/**
 * LoginPage.jsx — DeutschBridge Institute
 * Updated: "Forgot password?" link opens a modal with email input
 * that calls POST /api/auth/forgot-password.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiLogin, apiForgotPassword } from '../../utils/api';

const B = '#1A1A1A';
const R = '#DD0000';
const G = '#FFCE00';

function Flag() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: 26, height: 19, borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <div style={{ flex: 1, background: B }} />
      <div style={{ flex: 1, background: R }} />
      <div style={{ flex: 1, background: G }} />
    </div>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ── Forgot password modal ─────────────────────────────────────
function ForgotPasswordModal({ onClose }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSend = async () => {
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setError('');
    setLoading(true);
    try {
      await apiForgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    /* Backdrop */
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420,
        border: '1px solid #E0E0E0', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        animation: 'modal-in 0.2s ease both',
      }}>
        {/* Header stripe */}
        <div style={{ display: 'flex', height: 4 }}>
          <div style={{ flex: 1, background: B }} />
          <div style={{ flex: 1, background: R }} />
          <div style={{ flex: 1, background: G }} />
        </div>

        <div style={{ padding: '24px 24px 20px', fontFamily: 'Space Grotesk, sans-serif' }}>

          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: B }}>Forgot your password?</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#888', lineHeight: 1.5 }}>
                Enter your email and we'll send you a reset link.
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 22, lineHeight: 1, padding: '0 0 0 12px', marginTop: -2 }}>✕</button>
          </div>

          {sent ? (
            /* Sent state */
            <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>📬</div>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: B, marginBottom: 8 }}>Check your inbox!</h4>
              <p style={{ fontSize: 13, color: '#666', lineHeight: 1.65, marginBottom: 20 }}>
                If an account with <strong>{email}</strong> exists, a password reset link has been sent. 
                Please check your inbox (and spam folder).
              </p>
              <p style={{ fontSize: 12, color: '#aaa', marginBottom: 20 }}>
                The link expires in <strong>1 hour</strong>.
              </p>
              <button onClick={onClose} style={{
                padding: '11px 28px', background: R, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', minHeight: 44,
              }}>
                Back to Login
              </button>
            </div>
          ) : (
            /* Form state */
            <>
              {error && (
                <div style={{ padding: '10px 13px', background: '#FFF2F2', border: '1px solid #FECACA', borderRadius: 8, marginBottom: 14, fontSize: 13, color: '#DC2626', display: 'flex', gap: 7 }}>
                  <span>⚠️</span><span>{error}</span>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: B, marginBottom: 6 }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="you@example.com"
                  autoFocus
                  style={{
                    width: '100%', padding: '11px 14px', fontSize: 14, boxSizing: 'border-box',
                    border: '2px solid #E0E0E0', borderRadius: 9, outline: 'none', fontFamily: 'inherit',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e  => e.target.style.borderColor = R}
                  onBlur={e   => e.target.style.borderColor = '#E0E0E0'}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={{
                  flex: 1, padding: '11px', background: '#F5F5F5', color: '#555',
                  border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', minHeight: 44,
                }}>
                  Cancel
                </button>
                <button onClick={handleSend} disabled={loading} style={{
                  flex: 2, padding: '11px', background: loading ? '#ccc' : R,
                  color: '#fff', border: 'none', borderRadius: 9,
                  fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  minHeight: 44, transition: 'background 0.2s',
                }}>
                  {loading ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="40 20"/>
                      </svg>
                      Sending…
                    </>
                  ) : '📧 Send Reset Link'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <style>{`
        @keyframes modal-in { from { opacity:0; transform:scale(0.95) translateY(-10px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes spin     { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Main LoginPage ────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPw,      setShowPw]      = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [showForgot,  setShowForgot]  = useState(false);

  const handleLogin = async () => {
    if (!email.trim() || !password) { setError('Please enter your email and password.'); return; }
    setError('');
    setLoading(true);
    try {
      const data = await apiLogin(email.trim().toLowerCase(), password);
      localStorage.setItem('goethe_token', data.token);
      login(data.user);

      // Redirect based on role
      if (data.user.temp_password) {
        navigate('/change-password');
      } else if (data.user.role === 'super_admin') {
        navigate('/super-admin/dashboard');
      } else if (['teacher','admin'].includes(data.user.role)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/student/dashboard');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    fontSize: 14, boxSizing: 'border-box',
    border: '2px solid #E0E0E0', borderRadius: 10,
    outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Space Grotesk, sans-serif' }}>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

      <div style={{ maxWidth: 440, width: '100%' }}>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E0E0E0', overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.1)' }}>

          {/* Header */}
          <div style={{ background: B, padding: '22px 28px' }}>
            <div style={{ display: 'flex', height: 4, marginBottom: 18 }}>
              <div style={{ flex: 1, background: B, borderRight: '1px solid rgba(255,255,255,0.1)' }} />
              <div style={{ flex: 1, background: R }} />
              <div style={{ flex: 1, background: G }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Flag />
              <div>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', fontFamily: 'Syne, sans-serif' }}>
                  <span style={{ color: G }}>Deutsch</span>Bridge
                </span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', marginLeft: 6 }}>Institute</span>
              </div>
            </div>
          </div>

          {/* Form body */}
          <div style={{ padding: '30px 28px 24px' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: B }}>Welcome back</h2>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: '#888' }}>Log in to continue your German journey.</p>

            {error && (
              <div style={{ padding: '11px 14px', background: '#FFF2F2', border: '1px solid #FECACA', borderRadius: 10, marginBottom: 18, fontSize: 13, color: '#DC2626', display: 'flex', gap: 8 }}>
                <span>⚠️</span><span>{error}</span>
              </div>
            )}

            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: B, marginBottom: 6 }}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="your@email.com"
                style={inputStyle}
                onFocus={e  => e.target.style.borderColor = R}
                onBlur={e   => e.target.style.borderColor = '#E0E0E0'}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: B, marginBottom: 6 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  placeholder="Your password"
                  style={{ ...inputStyle, paddingRight: 44 }}
                  onFocus={e  => e.target.style.borderColor = R}
                  onBlur={e   => e.target.style.borderColor = '#E0E0E0'}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center', padding: 4 }}
                >
                  <EyeIcon open={showPw} />
                </button>
              </div>
            </div>

            {/* Forgot password link */}
            <div style={{ textAlign: 'right', marginBottom: 22 }}>
              <button
                onClick={() => setShowForgot(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: R, fontWeight: 600, padding: 0, fontFamily: 'inherit' }}
              >
                Forgot password?
              </button>
            </div>

            {/* Login button */}
            <button
              onClick={handleLogin}
              disabled={loading}
              style={{
                width: '100%', padding: '13px', background: loading ? '#ccc' : R,
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.2s', minHeight: 48,
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#BB0000'; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? '#ccc' : R; }}
            >
              {loading ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="40 20" />
                  </svg>
                  Signing in…
                </>
              ) : '→ Sign In'}
            </button>

            {/* Register link */}
            <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#888' }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: R, fontWeight: 600, textDecoration: 'none' }}>
                Enroll Now
              </Link>
            </p>
          </div>
        </div>

        {/* Support */}
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: '#aaa' }}>
          Need help? Call{' '}
          <a href="tel:0202071417" style={{ color: R, textDecoration: 'none', fontWeight: 600 }}>0202071417</a>
        </p>

        <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
